import React, { useState, useEffect } from 'react';
import { useDispatch } from 'react-redux';
import styled from 'styled-components';
import { Paper, Box } from '@material-ui/core';
import { Table, TableBody, TableCell, TableContainer, TableHead, TableRow } from '@material-ui/core';
import * as Linq from 'linq';
import { marked } from 'marked';
import DOMPurify from 'dompurify';

import { CardDb, updateCard } from '../redux/cardDbSlice';
import { ImageAttribute, CardAttributeDb, CardAttributeDbHashType, isMultiAttribute, isTableAttribute, isNondisplayAttribute, updateCardAttribute } from '../redux/cardAttributeDbSlice';
import { CardImageShape, CardShapeDb } from '../redux/cardShapeDbSlice';
import { FontType, FrameType } from '../redux/cardAttributeModelIndex';
import { getCardSize } from './index';
import useOneCardDb from '../hooks/useOneCardDb';
import useCardItemDbs from '../hooks/useCardItemDbs';
import useImageData from '../hooks/useImageData'

import { Hash, deepCopy, isSameObject, getFontData } from '../general';
import CircleShape from '../card_components/CircleShape';
import RectangleShape from '../card_components/RectangleShape';
import BalloonShape from '../card_components/BalloonShape';

type CardPaperProps = {
  preview: boolean;
  previewScale?: number;
  width: number;
  height: number;
  $frameDisplay: boolean;
  $frameColor: string;
  $backgroundDisplay: boolean;
  $backgroundColor: string;
}
const CardPaper = styled(Paper)<CardPaperProps>`
  position: relative;
  width: ${props => props.width + 'px'};
  height: ${props => props.height + 'px'};
  border: ${props => props.$frameDisplay ? '1px solid ' + props.$frameColor : undefined};
  background-color: ${props => props.$backgroundDisplay ? props.$backgroundColor : '#11ffee00'};
  overflow: hidden;
  box-sizing: border-box;
  ${props => (props.preview && props.previewScale) ? `transform: scale(${props.previewScale}); transform-origin: top left;` : undefined}
`
type TextDivProps = {
  x: number;
  y: number;
  width: number;
  height: number;
  rotation?: number;
  frame: FrameType;
  $backgroundColor?: string;
  selecting?: boolean;
} & {[P in keyof FontType]?: FontType[P]};
const TextDivBase = styled.div<TextDivProps>`
  position: absolute;
  display: grid;
  left: ${props => `${props.x}px`};
  top: ${props => `${props.y}px`};
  width: ${props => props.width + 'px'};
  height: ${props => props.height + 'px'};
  transform: ${props => props.rotation ? 'rotate(' + props.rotation + 'deg)' : undefined};
  transform-origin: center;
  border: ${props => {
    if (props.frame.display) return `${props.frame.thickness}px ${props.frame.color} ${props.frame.lineStyle}`;
    else return undefined;
  }};
  border-radius: ${props => {
    if (props.frame.display && props.frame.roundCorner) return `${props.frame.radius}px`;
    else return undefined;
  }};
  outline: ${props => props.selecting ? '2px solid #3f50b5' : undefined};
  box-sizing: border-box;
  background-color: ${props => props.$backgroundColor};
  font-size: ${props => props.size + 'px'};
  font-weight: ${props => props.bold ? 'bold' : 'normal'};
  color: ${props => props.color};
  font-style: ${props => props.italic ? 'italic' : 'normal'};
  text-decoration: ${props => props.underline ? 'underline' : undefined};
  font-family: ${props => props.name};
  text-align: ${props => props.yoko};
  align-items: ${props => props.tate};
  writing-mode: ${props => props.direction};
  text-orientation: ${props => props.orientation};
`
const TextDiv = styled(TextDivBase)`
  overflow-wrap: anywhere;
`
const SingleDiv = styled(TextDivBase)`
  overflow-wrap: anywhere;
  overflow-y: hidden;
`
const MultiDiv = styled(TextDivBase)`
  min-height: ${props => props.height + 'px'};
  height: auto;
`
const MultiContentDiv = styled.div`
  width: inherit;
  white-space: break-spaces;
  overflow-wrap: anywhere;
  hyphens: auto;
`
type TableDivProps = {
  x: number;
  y: number;
  width: number;
  height: number;
  selecting?: boolean;
}
const TableDiv = styled.div<TableDivProps>`
  position: absolute;
  left: ${props => {
    const x = props.selecting ? props.x - 2 : props.x;
    return x + 'px';
  }};
  top: ${props => {
    const y = props.selecting ? props.y - 2 : props.y;
    return y + 'px';
  }};
  width: ${props => props.width + 'px'};
  min-height: ${props => props.height + 'px'};
  height: auto;
  border: ${props => props.selecting ? '2px solid #3f50b5' : undefined};
`
type AttributeTableCellProps = {
  name?: string; //フォント名
  color?: string;
  fSize?: number;
  bold?: boolean; //太字
  italic?: boolean; //斜体
  yoko?: 'left' | 'center' | 'right'; //左右寄せ　'left':左　'center':中央 'right':右
}
const AttributeTableCell = styled(TableCell)<AttributeTableCellProps>`
  font-size: ${props => props.fSize + 'px'};
  font-weight: ${props => props.bold ? 'bold' : 'normal'};
  color: ${props => props.color};
  font-style: ${props => props.italic ? 'italic' : 'normal'};
  font-family: ${props => props.name};
  text-align: ${props => props.yoko};
`
type ImageDivProps = {
  x: number;
  y: number;
  width: number;
  height: number;
  rotation: number;
  frame: FrameType;
  $backgroundColor?: string;
  selecting: boolean;
}
const ImageDiv = styled.div<ImageDivProps>`
  position: absolute;
  transform: ${props => {
    if (props.width < 0) return `translate(${props.x + props.width}px, ${props.y}px) rotate(${props.rotation}deg) scaleX(-1) !important`;
    return `translate(${props.x}px, ${props.y}px) rotate(${props.rotation}deg) !important`;
  }};
  transform-origin: center;
  width: ${props => Math.abs(props.width) + 'px'};
  height: ${props => props.height + 'px'};
  border: ${props => {
    if (props.frame.display) return `${props.frame.thickness}px ${props.frame.color} ${props.frame.lineStyle}`;
    else return undefined;
  }};
  border-radius: ${props => {
    if (props.frame.display && props.frame.roundCorner) return `${props.frame.radius}px`;
    else return undefined;
  }};
  outline: ${props => props.selecting ? '2px solid #3f50b5' : undefined};
  box-sizing: border-box;
  background-color: ${props => props.$backgroundColor};
  overflow: hidden;
`
type AttributeImgProps = {
  x: number;
  y: number;
}
const AttributeImg = styled.img<AttributeImgProps>`
  position: absolute;
  left: ${props => props.x + 'px'};
  top: ${props => props.y + 'px'};
  user-select: none;
`

const isAttribute = (obj: any): obj is CardAttributeDb => {
  return typeof obj?.cardAttributeModel === 'string';
}
const isShape = (obj: any): obj is CardShapeDb => {
  return typeof obj?.kind === 'string';
}
const getLinkedSentences = (content: string) => {
  const sentences: { isLink: boolean; sentence: string; }[] = [];
  const linkWay = ' target="_blank" rel="noopener noreferrer"';
  const length = content.length;
  for (let i = 0; i <= length;) {
    const httpsStart = content.indexOf('https:', i);
    const httpStart = content.indexOf('http:', i);
    const urlStart = httpsStart >= 0 ? httpsStart : httpStart;
    if (urlStart === -1) {
      const sentence = content.slice(i, length);
      sentences.push({ isLink: false, sentence });
      break;
    }
    sentences.push({ isLink: false, sentence: content.slice(i, urlStart) });
    const urlEnd = content.indexOf(' ', urlStart + 1);
    if (urlEnd === -1) {
      const url = content.slice(urlStart, length);
      const sentence = `<a href="${url}" ${linkWay}>${url}</a>`;
      sentences.push({ isLink: true, sentence });
      break;
    } else {
      const url = content.slice(urlStart, urlEnd);
      const sentence = `<a href="${url}" ${linkWay}>${url}</a>`;
      sentences.push({ isLink: true, sentence });
      i = urlEnd;
    }
  }
  return sentences;
}

type SingleAttributeContentProps = {
  content: string;
}
const SingleAttributeContent: React.FC<SingleAttributeContentProps> = ({ content }) => {
  const getContent = () => {
    return getLinkedSentences(content);
  }

  return (
    <>
      { getContent().map(sentence =>
        sentence.isLink ? <div dangerouslySetInnerHTML={{ __html: sentence.sentence }} /> :
        <div>{ sentence.sentence }</div>
      ) }
    </>
  );
}

type MultiAttributeMarkdownProps = {
  value: string;
  checkItemDrawn: () => void;
  setElementUpdated: (value: boolean) => void;
}
const MultiAttributeMarkdown: React.FC<MultiAttributeMarkdownProps> = ({ value, checkItemDrawn, setElementUpdated }) => {
  const [content, setContent] = useState('');

  const getStyle = (isLink: boolean) => {
    return isLink ? ' style="margin-top: 0px; margin-bottom: 0px; pointer-events: auto;"' : ' style="margin-top: 0px; margin-bottom: 0px; pointer-events: none;"';
  }
  const setStyle = (html: string) => {
    const sentences: string[] = [];
    const linkWay = ' target="_blank" rel="noopener noreferrer"';
    const length = html.length;
    for (let i = 0; i <= length;) {
      const tagStart = html.indexOf('<', i);
      if (tagStart === -1) {
        const sentence = html.slice(i, length - 1);
        sentences.push(sentence);
        break;
      }
      if (html.at(tagStart + 1) === '/') {
        const sentence = html.slice(i, tagStart + 1);
        sentences.push(sentence);
        i = tagStart + 1;
      } else {
        const isLink = html.slice(tagStart + 1, tagStart + 3) === 'a ';
        const tagEnd = html.indexOf('>', tagStart + 1);
        const tag = ''.concat(html.slice(i, tagEnd), getStyle(isLink));
        const addition = isLink ? linkWay.concat('>') : '>';
        const sentence = tag.concat(addition);
        sentences.push(sentence);
        i = tagEnd + 1;
      }
    }
    const res = ''.concat(...sentences);
    return res;
  }
  const getContent = async () => {
    const dangerouslyHtml = await marked.parse(value);
    const clean = DOMPurify.sanitize(dangerouslyHtml, { USE_PROFILES: { html: true } });
    const res = setStyle(clean);
    setContent(res);
    checkItemDrawn();
    setElementUpdated(true);
  }

  useEffect(() => {
    getContent();
  }, [value])

  return (
    <MultiContentDiv dangerouslySetInnerHTML={{ __html: content }} />
  );
}

type MultiAttributeContentProps = {
  value: string;
}
const MultiAttributeContent: React.FC<MultiAttributeContentProps> = ({ value }) => {
  const cursors: number[] = [];
  const firstIx = value.indexOf('---\n');
  if (firstIx === 0) cursors.push(firstIx);
  for (let i = 0; i < value.length;) {
    const ix = value.indexOf('\n---\n', i);
    if (ix === -1) break;
    const targetIx = ix + 1;
    cursors.push(targetIx);
    i = targetIx + 3;
  }

  const contents: string[] = [];
  cursors.forEach((cursor, ix) => {
    if (ix === 0 && cursor !== 0) contents.push(value.substring(0, cursor));
    const divider = value.substring(cursor, cursor + 4);
    const nextCursor = typeof cursors[ix + 1] === 'number' ? cursors[ix + 1] : value.length;
    const content = value.substring(cursor + 4, nextCursor);
    contents.push(divider, content);
  })

  return (
    <>
      { contents.length >= 1 && contents.map((content, ix) => {
        if (content === '---\n') return ( <hr key={ix} style={{width: 'inherit', height: '1px', border: 'none', backgroundColor: '#000000'}} /> );
        else return (
          getLinkedSentences(content).map((sentence, ix) => (
            sentence.isLink ? <MultiContentDiv key={ix} dangerouslySetInnerHTML={{ __html: sentence.sentence }} /> :
            <MultiContentDiv key={ix}>{ sentence.sentence }</MultiContentDiv>
          ))
        );
      }) }
      { contents.length === 0 &&
        getLinkedSentences(value).map((sentence, ix) => (
          sentence.isLink ? <MultiContentDiv key={ix} dangerouslySetInnerHTML={{ __html: sentence.sentence }} /> :
          <MultiContentDiv key={ix}>{ sentence.sentence }</MultiContentDiv>
        ))
      }
    </>
  );
}

type RenderImageProps = {
  item: ImageAttribute | CardImageShape;
  selecting: boolean;
}
const RenderImage: React.FC<RenderImageProps> = ({item, selecting}) => {
  const imageData = isShape(item) ? item.image : item.content;
  const dataUrl = useImageData(imageData.imageId);
  const imgWidth = imageData.originWidth;
  const imgHeight = imageData.originHeight;
  const trimRatioWidth = Math.abs(item.width) / (imageData.trimXTo - imageData.trimXFrom);
  const trimRatioHeight = item.height / (imageData.trimYTo - imageData.trimYFrom);
  const translateX = -imageData.trimXFrom * trimRatioWidth;
  const translateY = -imageData.trimYFrom * trimRatioHeight;

  return (
    <ImageDiv x={item.x} y={isAttribute(item) ? item.currentY : item.y} width={item.width} height={item.height}
      rotation={item.rotation}
      frame={item.frame} $backgroundColor={item.background.display ? item.background.color : undefined}
      selecting={selecting}
    >
      <AttributeImg src={dataUrl} draggable="false" x={translateX} y={translateY}
        width={imgWidth * trimRatioWidth} height={imgHeight * trimRatioHeight} />
    </ImageDiv>
  );
}

type PreviewsType = {
  previewScale: number;
  card: CardDb;
  setCard: (card: CardDb) => void;
  ids: string[];
  values: (CardAttributeDb | CardShapeDb)[];
  setAttributes: (items: CardAttributeDbHashType) => void;
  editingId?: string;
}
type CommonCardProps = {
  cardId: string;
  previews?: PreviewsType;
  widthValue?: number;
  heightValue?: number;
}
const CommonCard: React.FC<CommonCardProps> = ({ cardId, previews, widthValue, heightValue }) => {
  const dispatch = useDispatch();
  const cardValue = useOneCardDb(cardId);
  const [ids, values] = useCardItemDbs(cardId);
  const card = previews ? previews.card : cardValue;
  const itemIds = previews ? previews.ids : ids;
  const items = previews ? previews.values : values;
  type ItemHashType = { [key in string]: CardAttributeDb | CardShapeDb; }
  const [itemHash, setItemHash] = useState<ItemHashType>({});
  const [elementUpdated, setElementUpdated] = useState(false);
  const [itemsDrawn, setItemsDrawn] = useState<Hash<boolean>>({});

  const saveCard = (value: CardDb) => {
    if (previews) previews.setCard(value);
    else dispatch(updateCard({ id: cardId, ...value }))
  }
  const saveAttributes = (values: CardAttributeDbHashType) => {
    if (previews) previews.setAttributes(values);
    else {
      const attributeIds = Object.keys(values);
      attributeIds.forEach(id => dispatch(updateCardAttribute({ id, ...values[id] })))
    }
  }
  const updateCardCurrentHeight = (items?: CardAttributeDbHashType) => {
    if (!card) return;
    const newCard = deepCopy(card);
    let cardHeight = getCardSize(newCard).height;
    const bottomY = Linq.from(itemIds).max(id => {
      const item = items ? items[id] : undefined;
      if (!item || isShape(item) || isNondisplayAttribute(item)) return 0;
      const itemHeight = (isMultiAttribute(item) || isTableAttribute(item)) && item.currentHeight ? item.currentHeight : item.height;
      return item.currentY + itemHeight;
    });
    if (bottomY !== cardHeight) {
      newCard.currentHeight = Math.max(bottomY, card.defaultHeight);
      saveCard(newCard);
    }
  }
  const changeCurrentHeight = (hash: ItemHashType, id: string, targetElement: Element): [CardAttributeDbHashType, boolean] => {
    const ids = Object.keys(hash);
    const newAttributes: CardAttributeDbHashType = {};
    let changed = false;
    ids.forEach(targetId => {
      const target = hash[targetId];
      if (targetId === id && (isMultiAttribute(target) || isTableAttribute(target))) {
        const height = target.currentHeight ? target.currentHeight : target.height;
        const actualHeight = target.frame.display ? targetElement.clientHeight + target.frame.thickness * 2 : targetElement.clientHeight;
        changed = actualHeight !== height;
        const currentHeight = actualHeight > target.height ? actualHeight : target.height;
        newAttributes[targetId] = { ...target, currentHeight };
      } else if (isAttribute(target)) newAttributes[targetId] = target;
    })
    return [newAttributes, changed];
  }
  const changeAttributesCurrentY = (items: CardAttributeDbHashType): CardAttributeDbHashType => {
    const newAttributes = deepCopy(items);
    const newIds = Object.keys(newAttributes);
    const idsOrderedByY = Linq.from(newIds).orderBy(id => items[id].y).toArray();
    if (idsOrderedByY.length <= 1) return items;
    idsOrderedByY.forEach((id, ix) => {
      const attribute = newAttributes[id];
      if (isNondisplayAttribute(attribute)) return;
      let anchor: { id: string | undefined, y: number } = { id: undefined, y: 0 };
      for (let i = 0; i < ix; i++) {
        const targetId = idsOrderedByY[i]
        const target = newAttributes[targetId];
        if (isNondisplayAttribute(target)) break;
        const diff = attribute.y - (target.y + target.height);
        const targetHeight = (isMultiAttribute(target) || isTableAttribute(target)) && target.currentHeight ? target.currentHeight : target.height;
        const targetBottomY = target.currentY + targetHeight + diff;
        if (target.x < attribute.x + attribute.width && attribute.x < target.x + target.width && targetBottomY > anchor.y) {
          anchor = { id: targetId, y: targetBottomY };
        }
      }
      if (typeof anchor.id !== 'string') {
        attribute.currentY = attribute.y;
        return;
      }
      attribute.currentY = anchor.y;
      newAttributes[id] = attribute;
    })
    return newAttributes;
  }
  const updateAttributes = (id?: string) => {
    // reactの仕様かわからないが、getElementsByClassNameで取得したElementの2つ目が実体のようなのでそちらを使う
    // デザイン編集で新規追加したアイテムに関しては1つしかないようなのでそちらを使う
    const targetElement = id ? document.getElementsByClassName(id)[1] || document.getElementsByClassName(id)[0] : undefined;
    if (!targetElement || !id) return;
    const target = itemHash[id];
    if (!isMultiAttribute(target) && !isTableAttribute(target)) return;
    const [attributesUpdatedHeight, changed] = changeCurrentHeight(itemHash, id, targetElement);
    if (!changed) return;
    const newAttributes = changeAttributesCurrentY(attributesUpdatedHeight);
    saveAttributes(newAttributes);
    // update card height
    updateCardCurrentHeight(newAttributes);
  }

  const checkItemDrawn = (id: string) => {
    const newItemsDrawn = deepCopy(itemsDrawn);
    newItemsDrawn[id] = true;
    setItemsDrawn(newItemsDrawn);
  }

  useEffect(() => {
    const newItemsDrawn: Hash<boolean> = {};
    itemIds.forEach((id, ix) => {
      const item = items[ix];
      if (!item) return;
      if (isMultiAttribute(item) && item.isMarkdown) newItemsDrawn[id] = false;
      else newItemsDrawn[id] = true;
    })
    setItemsDrawn(newItemsDrawn);
  }, [])

  useEffect(() => {
    let changed = false;
    if (Object.keys(itemHash).length === 0 && itemIds.length !== 0) changed = true;
    else {
      itemIds.forEach((id, ix) => {
        const a = items[ix];
        const b = itemHash[id];
        if (a === undefined && b === undefined) return;
        if (!isSameObject(a, b)) changed = true;
      })
    }
    if (!changed) return;
    const newHash: ItemHashType = {};
    itemIds.forEach((id, ix) => newHash[id] = items[ix])
    setItemHash(newHash);
  }, [items])

  useEffect(() => {
    const drawnComplete = Linq.from(Object.keys(itemsDrawn)).all(id => itemsDrawn[id]);
    if (!drawnComplete) return;
    itemIds.forEach(id => {
      updateAttributes(id);
    })
  }, [itemHash, itemsDrawn])

  useEffect(() => {
    if (!elementUpdated) return;
    itemIds.forEach(id => {
      updateAttributes(id);
    })
    setElementUpdated(false);
  }, [elementUpdated])

  if (!card) return <></>;
  return (
    <CardPaper className="magica-card" elevation={card.shadow ? 1 : 0}
      preview={Boolean(previews)} previewScale={previews ? previews.previewScale : undefined}
      width={widthValue ? widthValue : getCardSize(card).width} height={heightValue ? heightValue : getCardSize(card).height}
      $frameDisplay={card.frame.display} $frameColor={card.frame.color}
      $backgroundDisplay={card.background.display} $backgroundColor={card.background.color}
    >
      { itemIds.map((id, ix) => {
        const item = items[ix];
        if (isAttribute(item) && item.cardAttributeModel === 'single') {
          const { name, color, size, bold, italic, underline, direction, orientation, yoko, tate } = getFontData(item.font);
          return (
            <SingleDiv key={id}
              x={item.x} y={item.currentY} width={item.width} height={item.height}
              rotation={item.rotation} frame={item.frame} $backgroundColor={item.background.display ? item.background.color : undefined}
              selecting={id === previews?.editingId}
              size={size} bold={bold} color={color} italic={italic} underline={underline} name={name}
              yoko={yoko} tate={tate} direction={direction} orientation={orientation}>
              <SingleAttributeContent content={item.content} />
            </SingleDiv>
          );
        } else if(isAttribute(item) && item.cardAttributeModel === 'multi') {
          const { name, color, size, bold, italic, underline, yoko, tate } = item.font;
          return (
            <MultiDiv key={id}
              x={item.x} y={item.currentY} width={item.width} height={item.height}
              frame={item.frame} $backgroundColor={item.background.display ? item.background.color : undefined}
              selecting={id === previews?.editingId} className={id}
              size={size} bold={bold} color={color} italic={italic} underline={underline} name={name}
              yoko={yoko} tate={tate}>
              <Box width="100%">
                { item.isMarkdown ? <MultiAttributeMarkdown value={item.content} checkItemDrawn={() => checkItemDrawn(id)} setElementUpdated={setElementUpdated} /> : <MultiAttributeContent value={item.content} /> }
              </Box>
            </MultiDiv>
          );
        } else if (isAttribute(item) && item.cardAttributeModel === 'table') {
          const { name, color, size, bold, italic, yoko } = item.font;
          const columnIds = Object.keys(item.columnNames);
          const columnNames = columnIds.map(id => item.columnNames[id]);
          const rowIds = Object.keys(item.content);
          return (
            <TableDiv key={id}
              x={item.x} y={item.currentY} width={item.width} height={item.height}
              selecting={id === previews?.editingId} className={id}>
              <TableContainer style={{transform: 'scale(1)'}}>
                <Table stickyHeader size="small" aria-label='sticky table'>
                  <TableHead>
                    <TableRow>
                      { columnNames.map(columnName => (
                        <AttributeTableCell fSize={size} bold={bold} color={color} italic={italic} name={name}
                          yoko={yoko}>
                          {columnName}
                        </AttributeTableCell>
                      )) }
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    { rowIds.map(rowId => {
                      const row = item.content[rowId];
                      const values = columnIds.map(id => row.values[id]);
                      return (
                        <TableRow>
                          { columnIds.map((columnId, ix) =>
                            <AttributeTableCell fSize={size} bold={bold} color={color} italic={italic} name={name}
                              yoko={yoko}>
                              {values[ix]}
                            </AttributeTableCell>
                          ) }
                        </TableRow>
                      );
                    }) }
                  </TableBody>
                </Table>
              </TableContainer>
            </TableDiv>
          );
        } else if (isAttribute(item) && item.cardAttributeModel === 'image') {
          return (
            <RenderImage key={id} item={item} selecting={id === previews?.editingId} />
          );
        } else if (isShape(item) && item.kind === 'text') {
          const { name, color, size, bold, italic, underline, direction, orientation, yoko, tate } = getFontData(item.font);
          return (
            <TextDiv key={id}
              x={item.x} y={item.y}
              width={item.width} height={item.height} rotation={item.rotation}
              frame={item.frame} $backgroundColor={item.background.display ? item.background.color : undefined}
              size={size} bold={bold} color={color} italic={italic} underline={underline} name={name}
              yoko={yoko} tate={tate} direction={direction} orientation={orientation}>
              {item.text}
            </TextDiv>
          );
        } else if (isShape(item) && item.kind === 'circle') {
          return (
            <CircleShape key={id} item={item} />
          );
        } else if (isShape(item) && item.kind === 'rectangle') {
          return (
            <RectangleShape key={id} item={item} />
          );
        } else if (isShape(item) && item.kind === 'balloon') {
          return (
            <BalloonShape key={id} item={item} />
          );
        } else if (isShape(item) && item.kind === 'image') {
          return (
            <RenderImage key={id} item={item} selecting={false} />
          );
        } else return undefined;
      }) }
    </CardPaper>
  );
}

export default CommonCard;
